import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Ticket, Users, Home, Check, X, Loader2, ExternalLink, Sparkles, Download, Printer } from "lucide-react";

const TIER_META = {
  solo:    { color: "#D4AF37", icon: Ticket },
  duo:     { color: "#EEDC82", icon: Users },
  familie: { color: "#8FBC8F", icon: Home  },
};

const fmt = (iso) => { try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return iso || "—"; } };

export default function AboTab() {
  const [state, setState] = useState({ subscription: null, plan: null, active: false });
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [{ data: me }, { data: p }] = await Promise.all([
        api.get("/member/subscription"),
        api.get("/subscriptions/plans"),
      ]);
      setState(me); setPlans(p.plans || []);
    } catch (e) { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const subscribe = async (tier) => {
    setBusy(true);
    try {
      const { data } = await api.post("/member/subscription/checkout", { tier, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler beim Start des Kaufs");
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Abo zum Ende der Laufzeit kündigen?")) return;
    setBusy(true);
    try { await api.post("/member/subscription/cancel"); toast.success("Kündigung gespeichert"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler"); }
    finally { setBusy(false); }
  };
  const resume = async () => {
    setBusy(true);
    try { await api.post("/member/subscription/resume"); toast.success("Weiter geht's!"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler"); }
    finally { setBusy(false); }
  };

  // Aktives Abo → Statuskarte
  if (state.active && state.subscription) {
    const s = state.subscription;
    const meta = TIER_META[s.tier] || TIER_META.solo;
    const Icon = meta.icon;
    const plan = state.plan || {};
    return (
      <div className="space-y-6" data-testid="member-abo-active">
        <div className="card-lux p-6 relative overflow-hidden">
          <div className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{ background: `radial-gradient(circle at top right, ${meta.color}, transparent 60%)` }} />
          <div className="relative flex items-start gap-5">
            <div className="w-16 h-16 rounded-full flex items-center justify-center shrink-0" style={{ background: `${meta.color}22`, color: meta.color }}>
              <Icon size={26} />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.35em]" style={{ color: meta.color }}>Dein Abo · {s.source === "online" ? "Stripe" : "an der Kasse aktiviert"}</div>
              <div className="font-heading text-4xl mt-1">{plan.label} <span className="text-lg text-[#A8A398]">· {plan.price_monthly?.toFixed(0)} € / Monat</span></div>
              <div className="text-sm text-[#A8A398] italic mt-1">{plan.tagline}</div>
              <div className="mt-4 grid sm:grid-cols-2 gap-2 text-sm">
                <div><span className="text-[#575148] text-[10px] uppercase tracking-widest block">Aktiv seit</span> {fmt(s.activated_at)}</div>
                {s.expires_at && <div><span className="text-[#575148] text-[10px] uppercase tracking-widest block">{s.cancel_at_period_end ? "Endet am" : "Nächste Abbuchung"}</span> {fmt(s.expires_at)}</div>}
              </div>
              {plan.features && (
                <ul className="mt-5 space-y-2">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm">
                      <Check size={14} className="mt-0.5 shrink-0" style={{ color: meta.color }} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-6 flex items-center gap-3 flex-wrap">
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/member/abo-card/pdf`} target="_blank" rel="noreferrer"
                  data-testid="abo-download-card"
                  className="text-[10px] uppercase tracking-widest px-4 py-2 border rounded-full inline-flex items-center gap-2 hover:opacity-80"
                  style={{ borderColor: `${meta.color}66`, color: meta.color }}>
                  <Download size={12} /> Abo-Karte als PDF
                </a>
                {s.cancel_at_period_end ? (
                  <>
                    <div className="text-[10px] uppercase tracking-widest text-[#EE9CA1] px-3 py-1.5 border border-[#EE9CA1]/40 rounded-full">
                      Kündigung zum {fmt(s.expires_at)}
                    </div>
                    <button onClick={resume} disabled={busy} data-testid="abo-resume"
                      className="text-[10px] uppercase tracking-widest px-4 py-2 border border-[#D4AF37]/50 text-[#D4AF37] rounded-full hover:bg-[#D4AF37]/10">
                      Kündigung zurücknehmen
                    </button>
                  </>
                ) : (
                  <button onClick={cancel} disabled={busy} data-testid="abo-cancel"
                    className="text-[10px] uppercase tracking-widest px-4 py-2 border border-[#2A2A2A] text-[#A8A398] rounded-full hover:border-[#EE9CA1] hover:text-[#EE9CA1]">
                    Zum Monatsende kündigen
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Kein Abo → Plans anzeigen
  return (
    <div className="space-y-6" data-testid="member-abo-plans">
      <div className="text-center max-w-2xl mx-auto">
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] mb-3"><Sparkles size={12} /> Movie House Abo</div>
        <h2 className="font-heading text-4xl mb-3">Ein Preis. Unbegrenzt Kino.</h2>
        <p className="text-sm text-[#A8A398]">Monatlich abbuchbar, jederzeit kündbar. Wähle den Tarif der zu dir passt.</p>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {plans.map((p) => {
          const meta = TIER_META[p.tier] || TIER_META.solo;
          const Icon = meta.icon;
          const highlight = p.tier === "duo";
          return (
            <div key={p.tier} data-testid={`abo-card-${p.tier}`}
              className={`card-lux p-6 flex flex-col relative overflow-hidden ${highlight ? "ring-1 ring-[#D4AF37]/50" : ""}`}>
              {highlight && (
                <div className="absolute top-3 right-3 text-[9px] uppercase tracking-widest bg-[#D4AF37] text-black px-2 py-0.5 rounded">Beliebt</div>
              )}
              <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ background: `${meta.color}22`, color: meta.color }}>
                <Icon size={22} />
              </div>
              <div className="text-[10px] uppercase tracking-widest" style={{ color: meta.color }}>{p.label}</div>
              <div className="font-heading text-4xl mt-1">{p.price_monthly.toFixed(0)} €<span className="text-sm text-[#A8A398]"> / Monat</span></div>
              <div className="text-xs text-[#A8A398] italic mt-1">{p.tagline}</div>
              <ul className="mt-5 space-y-2 flex-1">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <Check size={14} className="mt-0.5 shrink-0" style={{ color: meta.color }} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button onClick={() => subscribe(p.tier)} disabled={busy}
                data-testid={`abo-subscribe-${p.tier}`}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 py-3 rounded-full text-[11px] uppercase tracking-[0.4em] transitions-safe disabled:opacity-40"
                style={{ background: meta.color, color: "#0A0A0A" }}>
                {busy ? <Loader2 size={14} className="animate-spin" /> : <>{p.label} abonnieren <ExternalLink size={12} /></>}
              </button>
            </div>
          );
        })}
      </div>
      <div className="text-center text-[10px] text-[#575148] uppercase tracking-widest">
        Zahlung sicher über Stripe · SEPA + Kreditkarte · Kein Vertrag, jederzeit kündbar
      </div>
    </div>
  );
}
